import { AppLayout } from "@/components/layout/AppLayout";
import {
  NavigationMenu,
  NavigationMenuHeader,
  NavigationMenuHeading,
  NavigationMenuLink,
} from "@/components/layout/NavigationMenu";
import { MoneyFnsProvider } from "@/modules/money-fns";
import { checkAdminRole, checkAuth } from "@/utils/router";
import { seo } from "@/utils/seo";
import { ChevronLeftIcon } from "@givenwell/core-solid-icons";
import { useInterval } from "@givenwell/hooks";
import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Outlet, useCanGoBack, useRouter } from "@tanstack/react-router";
import { Fragment, useState } from "react";

const FIVE_MINUTES_IN_MILLIS = 5 * 60 * 1000;

export const Route = createFileRoute("/dashboard")({
  beforeLoad: async () => {
    await checkAuth();
    await checkAdminRole();
  },
  component: Layout,
  head: () => ({
    meta: seo({ title: "Dashboard" }),
  }),
});

type LinkGroup = {
  name: string;
  links: { name: string; pathname: string }[];
};

const REPORTS = [
  {
    name: "KPIs",
    links: [
      { name: "Combined", pathname: "/dashboard/" },
      { name: "MRR", pathname: "/dashboard/mrr" },
      { name: "Utilisation", pathname: "/dashboard/utilisation" },
      { name: "EAP Utilisation", pathname: "/dashboard/eap-utilisation" },
      { name: "Spending", pathname: "/dashboard/spending" },
      { name: "Coverage", pathname: "/dashboard/coverage" },
    ],
  },
  {
    name: "Nerd Stuff",
    links: [
      { name: "Feature Usage", pathname: "/dashboard/usage" },
      { name: "Costs", pathname: "/dashboard/costs" },
      { name: "Cash Flow", pathname: "/dashboard/cash-flow" },
      { name: "Listing Prices", pathname: "/dashboard/listing-prices" },
      { name: "TV", pathname: "/dashboard/tv" },
      { name: "TV (Trent’s Version)", pathname: "/dashboard/tv2" },
    ],
  },
] satisfies LinkGroup[];

function Layout() {
  const [hideSidebar, setHideSidebar] = useState(false);
  const canGoBack = useCanGoBack();
  const router = useRouter();

  const qc = useQueryClient();

  useInterval(() => {
    qc.invalidateQueries({
      queryKey: [],
      exact: false,
      type: "active",
    });
  }, FIVE_MINUTES_IN_MILLIS);

  return (
    <AppLayout
      role="admin"
      sidebar={
        <NavigationMenu header={<NavigationMenuHeader />}>
          <NavigationMenuLink
            to="/admin"
            icon={<ChevronLeftIcon />}
            onClick={e => {
              if (canGoBack) {
                e.preventDefault();
                router.history.back();
              }
            }}
          >
            Back to admin
          </NavigationMenuLink>
          {REPORTS.map((group, i) => (
            <Fragment key={i}>
              {group.name && <NavigationMenuHeading>{group.name}</NavigationMenuHeading>}
              {group.links.map(link => (
                <NavigationMenuLink
                  key={link.pathname}
                  to={link.pathname}
                  activeOptions={{ exact: true, includeSearch: false }}
                  replace
                >
                  {link.name}
                </NavigationMenuLink>
              ))}
            </Fragment>
          ))}
        </NavigationMenu>
      }
    >
      <MoneyFnsProvider>
        <Outlet />
      </MoneyFnsProvider>
    </AppLayout>
  );
}
